/**
 * Connections API Service
 * 
 * Communicates with connections-service for user API/MCP connection tracking.
 * Replaces direct Firestore access with REST API calls.
 */

import { getIdToken } from './firebase';

async function authGet(url: string): Promise<Response> {
  const token = await getIdToken();
  return fetch(url, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
}

async function authPost(url: string, body: any): Promise<Response> {
  const token = await getIdToken();
  return fetch(url, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

async function authDelete(url: string): Promise<Response> {
  const token = await getIdToken();
  return fetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
}

const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
const CONNECTIONS_URL = `${ORBIT_GATEWAY_URL}/api/connections`;

export interface Connection {
  service_id: string;
  type: 'rest_api' | 'mcp';
  connected: boolean;
  connected_at?: string;
  last_used?: string;
  settings?: Record<string, any>;
  scopes?: string[];
}

export interface ConnectionStatus {
  connected: boolean;
  last_used?: string;
  settings?: Record<string, any>;
}

/**
 * List all connections for current user
 */
export async function listConnections(): Promise<Connection[]> {
  const response = await authGet(`${CONNECTIONS_URL}/me`);
  const data = await response.json();
  return data.connections || [];
}

/**
 * Get specific connection status
 */
export async function getConnection(serviceId: string): Promise<ConnectionStatus> {
  try {
    const response = await authGet(`${CONNECTIONS_URL}/me/${serviceId}`);
    const data = await response.json();
    return {
      connected: data.connected,
      last_used: data.last_used,
      settings: data.settings
    };
  } catch (error: any) {
    if (error.status === 404) {
      return { connected: false };
    }
    throw error;
  }
}

/**
 * Check multiple connection statuses at once
 */
export async function checkConnections(serviceIds: string[]): Promise<Record<string, ConnectionStatus>> {
  const response = await authPost(`${CONNECTIONS_URL}/me/check`, {
    service_ids: serviceIds
  });
  const data = await response.json();
  return data.statuses || {};
}

/**
 * Add a new connection (after OAuth flow)
 */
export async function addConnection(
  serviceId: string, 
  type: 'rest_api' | 'mcp' = 'rest_api',
  settings: Record<string, any> = {},
  scopes: string[] = []
): Promise<Connection> {
  const response = await authPost(`${CONNECTIONS_URL}/me`, {
    service_id: serviceId,
    type,
    settings,
    scopes
  });
  return await response.json();
}

/**
 * Remove a connection
 */
export async function removeConnection(serviceId: string): Promise<void> {
  await authDelete(`${CONNECTIONS_URL}/me/${serviceId}`);
}

/**
 * Update connection settings
 */
export async function updateConnection(
  serviceId: string, 
  settings: Record<string, any>
): Promise<void> {
  await authPost(`${CONNECTIONS_URL}/me/${serviceId}`, {
    settings
  });
}

/**
 * Check if specific integration is available in T8
 * (used before showing connect button)
 */
export async function isIntegrationAvailable(_serviceId: string): Promise<boolean> {
  // This would query T8 to see if the service exists
  // For now, assume all requested services are available or will be built
  return true;
}
